import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import { Space, Button } from 'antd';
import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import { NOC_DRILLDOWN, isAppModule } from '@utils/constants';
import track, {
  CLICK_ON_DELETE_DASHBOARD,
  CLICK_ON_UPDATE_DASHBOARD,
  CLICK_SAVE_FROM_KEBAB,
} from '@src/utils/sensor/track';
import { exportData } from '@store/export/actions';
import { setActiveDashboardId } from '@store/dashboards/ui/actions';
import { selector as defaultLandingPageSelector } from '@store/defaultLandingPage';
import {
  setDefaultLandingPage,
  setDefaultLandingPagePromptNeedInput,
  setDefaultLandingPagePromptTitle,
  setDefaultLandingPagePromptContent,
  setDefaultLandingPagePromptOkFn,
  showPrompt,
} from '@store/defaultLandingPage/actions';
import commonSelector, {
  annotationsFeatureSelector,
  horizontalLayoutSelector,
  realTimeModeOnTrendsSelector,
  publicSavedDashboardsSelector,
  appExperienceShareAndSaveSelector,
} from '@store/common/selectors';
import { modeSelector } from '@store/trends/selectors';
import featureNames from '@utils/featureToggle/constants';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import { alertSelector } from '@store/trends/alerts/selectors';
import { aiAlertSelector } from '@store/trends/aiAlerts/selectors';
import { prefixedCheckFeature } from '@utils/platform/features/checkFeature';
import { showRollingWindow } from '@utils/dashboard/rollingWindow';
import { SHARE_SERVICE_DASHBOARD_KEYS } from '@components/share/utils';
import Share from '@components/share/Share';
import SavingWrapper from '@components/saving/SavingWrapper/SavingWrapper';
import DeleteSavedDashboardWrapper from '@components/saving/DeleteSavedDashboardWrapper/DeleteSavedDashboardWrapper';
import { MODE_ALERT_DIAGNOSTICS, MODE_AI_ALERT_DIAGNOSTICS } from '@store/trends/constants';
import ExportOption from './ExportOption';
import getActualShareLink from '../../common/Sharing/SharingModal/SharingContent/getActualShareLink';
import DefaultPageOption from './Options/DefaultPageOption';
import { Header } from './Options/styled';
import { getShareConfig } from './utils';
import LatchShare from './LatchShare';
import ResetPersistence from '@components/common/ResetPersistence';

const { baseUrl } = window.conviva;

// Copied over from current options dropdown
// TODO: cleanup

const cf = prefixedCheckFeature('trends.actions');

class OptionList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dlpTitle: '',
      dlpContent: '',
      dlpOkFn: '',
      dlpNeedInput: '',
    };
  }

  shouldComponentUpdate(nextProps) {
    const { sharingState, defaultLandingPage, showMask }: any = this.props;
    return (
      !isEqual(nextProps.sharingState, sharingState) ||
      !isEqual(nextProps.defaultLandingPage.url, defaultLandingPage.url) ||
      !isEqual(nextProps.showMask, showMask)
    );
  }

  onExport = (context) => {
    const { exportData: dispatchExportData }: any = this.props;
    dispatchExportData(context);
  };

  onSetDefaultLandingApply = () => {
    const {
      cid,
      setDefaultLandingPage: dispatchSetDefaultLandingPage,
      mode,
      alert,
    }: any = this.props;
    const { pathname } = window.location;
    const arr = pathname.split('/');
    const u = getActualShareLink(pathname, cid);
    if (mode === MODE_ALERT_DIAGNOSTICS) {
      dispatchSetDefaultLandingPage({ url: u, name: `Alert: ${alert.name}`, path: pathname });
    } else {
      dispatchSetDefaultLandingPage({ url: u, name: arr[arr.length - 1], path: pathname });
    }
  };

  onSetDefaultLanding = () => {
    const { defaultLandingPage }: any = this.props;
    const {
      setActiveDashboardId: dispatchSetActiveDashboardId,
      setDefaultLandingPagePromptNeedInput: dispatchSetDefaultLandingPagePromptNeedInput,
      setDefaultLandingPagePromptTitle: dispatchSetDefaultLandingPagePromptTitle,
      setDefaultLandingPagePromptContent: dispatchSetDefaultLandingPagePromptContent,
      setDefaultLandingPagePromptOkFn: dispatchSetDefaultLandingPagePromptOkFn,
      showPrompt: showDefaultLandingPrompt,
    }: any = this.props;
    const hasDefaultLandingPage = defaultLandingPage.url.length > 0;
    const dlpContent = hasDefaultLandingPage
      ? 'Are you sure you want to change your default dashboard?'
      : 'Are you sure you want to set your default dashboard?';
    const obj = {
      dlpTitle: 'Make default',
      dlpContent,
      dlpOkFn: () => this.onSetDefaultLandingApply(),
      dlpNeedInput: false,
    };
    dispatchSetActiveDashboardId(null);
    dispatchSetDefaultLandingPagePromptNeedInput(obj.dlpNeedInput);
    dispatchSetDefaultLandingPagePromptTitle(obj.dlpTitle);
    dispatchSetDefaultLandingPagePromptContent(obj.dlpContent);
    dispatchSetDefaultLandingPagePromptOkFn(obj.dlpOkFn);
    this.setState(obj);
    showDefaultLandingPrompt();
  };

  render() {
    const {
      sharingState,
      share,
      features,
      defaultLandingPage,
      isSharing,
      mode,
      id,
      liveModeSelected,
      horizontalLayout,
      pageKeyForSaveService,
      isSavedDashboard,
      profileId,
      savedDashboardCreatorId,
      publicSavedDashboardsFeature,
      appExperienceShareAndSaveFeature,
      interval,
    }: any = this.props;

    const showShareOption = isAppModule
      ? appExperienceShareAndSaveFeature && !liveModeSelected
      : share && cf('share', true);
    const setDefaultLandingFlag = features[featureNames.DEFAULT_LANDING_PAGE] || true;
    const { dlpTitle, dlpContent, dlpNeedInput, dlpOkFn }: any = this.state;
    const { pathname } = window.location;
    const defaultPathname = defaultLandingPage.url || '';
    const isDefaultLandingPage = defaultPathname.endsWith(pathname);
    const hideMakeDefaultOption =
      isSharing || mode === NOC_DRILLDOWN || mode === MODE_AI_ALERT_DIAGNOSTICS;

    const shouldShowSaveOption = () => {
      return (
        horizontalLayout &&
        ((!isAppModule && publicSavedDashboardsFeature) ||
          (isAppModule && appExperienceShareAndSaveFeature)) &&
        // show save option if not a saved dashboard,
        // or show update option if it's a saved dashboard, and creatorId matches current profileId
        (!isSavedDashboard || savedDashboardCreatorId === profileId) &&
        // check for app save and share FF only if it is in app module
        cf('save', true)
      );
    };

    const shouldShowDeleteOption = () => {
      return (
        isSavedDashboard &&
        ((!isAppModule && publicSavedDashboardsFeature) ||
          (isAppModule && appExperienceShareAndSaveFeature)) &&
        savedDashboardCreatorId === profileId &&
        // check for app save and share FF only if it is in app module
        (!isAppModule || appExperienceShareAndSaveFeature)
      );
    };

    const onSaveClick = () => {
      track({ event: CLICK_SAVE_FROM_KEBAB });
    };

    const onUpdateClick = () => {
      track({ event: CLICK_ON_UPDATE_DASHBOARD });
    };

    const onDeleteClick = () => {
      track({ event: CLICK_ON_DELETE_DASHBOARD });
    };

    const pageKey = isAppModule
      ? SHARE_SERVICE_DASHBOARD_KEYS.APP_EXPERIENCE
      : SHARE_SERVICE_DASHBOARD_KEYS.TRENDS;
    const shareConfig = getShareConfig(baseUrl, isAppModule, mode);

    return (
      <>
        <LatchShare sharingState={sharingState} pageKey={pageKey} />
        <Space direction="vertical">
          <Header>Actions</Header>
          {showShareOption && (
            <Share
              config={shareConfig}
              pageKeyForSaveService={pageKeyForSaveService}
              id={id}
              withIcon={true}
            />
          )}
          {shouldShowSaveOption() && (
            <SavingWrapper
              pageMeta={{
                page: {
                  key: pageKeyForSaveService,
                },
                showRollingWindowOption: showRollingWindow(interval),
              }}
            >
              {isSavedDashboard ? (
                <Button onClick={onUpdateClick} type="text" size="small" icon={<SaveOutlined />}>
                  Update
                </Button>
              ) : (
                <Button onClick={onSaveClick} type="text" size="small" icon={<SaveOutlined />}>
                  Save
                </Button>
              )}
            </SavingWrapper>
          )}
          {shouldShowDeleteOption() && (
            <DeleteSavedDashboardWrapper
              pageMeta={{
                id,
              }}
              dashboardTypeName={isAppModule ? 'App_Experience' : 'TRENDS'}
            >
              <Button onClick={onDeleteClick} type="text" size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </DeleteSavedDashboardWrapper>
          )}
          <ExportOption />
          {setDefaultLandingFlag && !hideMakeDefaultOption && cf('make-default', true) && (
            <DefaultPageOption
              context={null}
              clickCallback={this.onSetDefaultLanding}
              disabled={isDefaultLandingPage}
              showIcon={true}
            />
          )}
          <ResetPersistence />
        </Space>
        <DefaultLandingPrompt
          title={dlpTitle}
          content={dlpContent}
          onOkFn={dlpOkFn}
          needInput={dlpNeedInput}
        />
      </>
    );
  }
}

const selector = createSelector(
  commonSelector,
  defaultLandingPageSelector,
  modeSelector,
  alertSelector,
  aiAlertSelector,
  realTimeModeOnTrendsSelector,
  horizontalLayoutSelector,
  annotationsFeatureSelector,
  publicSavedDashboardsSelector,
  appExperienceShareAndSaveSelector,
  (
    commonStore,
    defaultLandingPage,
    modeStore,
    alertStore,
    aiAlertStore,
    liveModeSelected,
    horizontalLayout,
    annotationsFeature,
    publicSavedDashboardsFeature,
    appExperienceShareAndSaveFeature,
  ) => ({
    features: commonStore.features,
    defaultLandingPage,
    cid: commonStore.profiles.filter((p) => p.is_selected)[0].customer_id,
    profileId: commonStore.profiles.filter((p) => p.is_selected)[0].profile_id,
    showMask: commonStore.showMask,
    mode: modeStore.mode,
    alert: alertStore.alert,
    aiAlert: aiAlertStore,
    isSharing: commonStore.isSharingPage,
    liveModeSelected,
    horizontalLayout,
    annotationsFeature,
    publicSavedDashboardsFeature,
    appExperienceShareAndSaveFeature,
  }),
);

export default connect(selector, {
  setActiveDashboardId,
  exportData,
  setDefaultLandingPage,
  setDefaultLandingPagePromptNeedInput,
  setDefaultLandingPagePromptTitle,
  setDefaultLandingPagePromptContent,
  setDefaultLandingPagePromptOkFn,
  showPrompt,
})(OptionList);
